import { EmptyState, SelectField } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import { type ReactElement, useState } from 'react'
import type { ConsentView, Translate } from './customer-types'

/** Props for {@link ConsentPanelView}; `onRecord` only when the viewer may record consent. */
export interface ConsentPanelViewProps {
	t: Translate
	current: ConsentView[]
	history: ConsentView[]
	onRecord?: (input: { channel: string; purpose: string; status: string }) => void
}

const CHANNELS = ['email', 'sms', 'whatsapp', 'phone', 'any']
const PURPOSES = ['marketing', 'profiling']
const STATUSES = ['granted', 'withdrawn', 'pending_confirmation']

function ConsentRow({ t, consent }: { t: Translate; consent: ConsentView }) {
	return (
		<tr>
			<td>{t(`channel.${consent.channel}`)}</td>
			<td>{t(`consents.purposes.${consent.purpose}`)}</td>
			<td>{t(`consents.statuses.${consent.status}`)}</td>
			<td>{consent.legalText ?? '—'}</td>
			<td>{consent.evidenceRedacted ? t('consents.redacted') : (consent.evidence ?? '—')}</td>
			<td>{consent.effectiveAt}</td>
		</tr>
	)
}

function ConsentTable({ t, rows, title }: { t: Translate; rows: ConsentView[]; title: string }) {
	return (
		<table className="w-full text-ds-sm" aria-label={title}>
			<thead>
				<tr className="text-left text-ds-text-muted">
					{[
						'consents.channel',
						'consents.purpose',
						'consents.status',
						'consents.legalText',
						'consents.evidence',
						'activity.when',
					].map((key) => (
						<th key={key} scope="col">
							{t(key)}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((consent) => (
					<ConsentRow key={consent.id} t={t} consent={consent} />
				))}
			</tbody>
		</table>
	)
}

/** Current consent per channel/purpose, the full history, and the record form (organism). */
export function ConsentPanelView(props: ConsentPanelViewProps): ReactElement {
	const { t } = props
	const [draft, setDraft] = useState({ channel: 'email', purpose: 'marketing', status: 'granted' })
	const select = (key: keyof typeof draft, values: string[], prefix: string) => (
		<SelectField
			label={t(`consents.${key}`)}
			value={draft[key]}
			onValueChange={(value) => setDraft({ ...draft, [key]: value })}
			options={values.map((v) => ({ id: v, text: t(`${prefix}${v}`) }))}
		/>
	)
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-ds-base font-semibold text-ds-text">{t('consents.current')}</h2>
			{props.current.length === 0 ? (
				<EmptyState title={t('consents.empty')} />
			) : (
				<ConsentTable t={t} rows={props.current} title={t('consents.current')} />
			)}
			<h2 className="text-ds-base font-semibold text-ds-text">{t('consents.history')}</h2>
			<ConsentTable t={t} rows={props.history} title={t('consents.history')} />
			{props.onRecord ? (
				<div className="flex items-end gap-2">
					{select('channel', CHANNELS, 'channel.')}
					{select('purpose', PURPOSES, 'consents.purposes.')}
					{select('status', STATUSES, 'consents.statuses.')}
					<Button onPress={() => props.onRecord?.(draft)}>{t('actions.recordConsent')}</Button>
				</div>
			) : null}
		</section>
	)
}
